import React from "react";
import { Table, TableHead, TableBody, TableRow, TableCell, Typography, Tooltip, Box } from "@mui/material";
import { DateTime } from "luxon";
import { formatDateAsLocaleDateString } from "utils/formatters/DateFormatters";
import type { TermExtension } from "client/api-v2";

interface SubscriptionExtensionHistoryProps {
  extensions: TermExtension[] | undefined;
}

export function SubscriptionExtensionHistory({ extensions }: SubscriptionExtensionHistoryProps) {
  if (!extensions || extensions.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          No term extensions have been applied to this subscription.
        </Typography>
      </Box>
    );
  }

  // Sort extensions by most recent first
  const sortedExtensions = [...extensions].sort((a, b) => {
    if (!a.extendedAt || !b.extendedAt) return 0;

    const dateA = DateTime.fromISO(a.extendedAt);
    const dateB = DateTime.fromISO(b.extendedAt);

    if (!dateA.isValid || !dateB.isValid) return 0;

    return dateB.toMillis() - dateA.toMillis();
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    return formatDateAsLocaleDateString(DateTime.fromISO(dateString, { zone: "utc" }), false);
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    const dt = DateTime.fromISO(dateString, { zone: "utc" });
    return dt.toFormat("yyyy-MM-dd HH:mm:ss");
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date Extended</TableCell>
            <TableCell>Extended By</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Previous End Date</TableCell>
            <TableCell>New End Date</TableCell>
            <TableCell>Days Added</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedExtensions.map((extension, index) => (
            <TableRow key={index}>
              <TableCell sx={{ minWidth: "95px", maxWidth: "95px" }}>
                <Typography variant="body2">{formatDateTime(extension.extendedAt)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{extension.extendedBy || "N/A"}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{extension.extensionType || "N/A"}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(extension.previousTermEndDate)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(extension.newTermEndDate)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {extension.daysAdded !== null && extension.daysAdded !== undefined
                    ? `${extension.daysAdded} days`
                    : "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                {extension.reason && extension.reason.length > 50 ? (
                  <Tooltip title={extension.reason} arrow>
                    <Typography variant="body2" sx={{ cursor: "help" }}>
                      {extension.reason.substring(0, 50)}...
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body2">{extension.reason || "N/A"}</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
